import React, { useState } from "react";
import { Table, Select, Button, Spin, Tag } from "antd";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useGetEventsQuery } from "../../services/events";
import { useGetEventComparisonQuery } from "../../services/analytics";
import * as XLSX from "xlsx";

const { Option } = Select;

const EventComparison = () => {
    const [selectedEvents, setSelectedEvents] = useState([]);

    const { data: eventsData, isLoading: isLoadingEvents } = useGetEventsQuery({
        currentPage: 1,
        resultPerPage: 1000
    });

    const events = eventsData?.events?.data || [];
    const eventIdsString = selectedEvents.join(',');

    const { data: comparisonData, isFetching: isLoadingComparison } = useGetEventComparisonQuery(
        eventIdsString,
        { skip: selectedEvents.length === 0 }
    );

    const tableData = comparisonData?.data || [];

    // Prepare region-wise pie data (use region string; fall back to location)
    const regionCounts = tableData.reduce((acc, item) => {
        const key = item.region || item.location || "Unknown";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    const regionPieData = Object.entries(regionCounts).map(([name, value]) => ({
        name,
        value,
    }));

    const pieColors = ["#FF9F7A", "#57C5B6", "#91CC75", "#5470C6", "#EE6666", "#73C0DE", "#FAC858"];

    const handleExport = () => {
        if (!tableData || tableData.length === 0) return;
        const exportData = tableData.map(item => ({
            "Event Name": item.eventName,
            "Scope": item.scope || "",
            "Region": item.region || "",
            "Total Delegates": item.totalDelegates,
            "Total Sponsors": item.totalSponsors,
            "Total Attendees": item.totalAttendees,
            "Total Meetings": item.totalMeetings,
            "Pending Meetings": item.pendingMeetings,
            "Acceptance Rate %": item.acceptanceRate,
            "Rejection Rate %": item.rejectionRate,
            "Cancellation %": item.cancellationPercent,
            "Total Show-ups (All)": item.totalShowUps,
            "Delegate Show-ups (All Days)": item.delegateShowUps,
            "Delegate Day 1 Attendance": item.delegateDay1Attendance,
            "Delegate Day 2 Attendance": item.delegateDay2Attendance,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Event Comparison");
        XLSX.writeFile(wb, "Event_Comparison_Report.xlsx");
    };

    const columns = [
        {
            title: 'Event Name',
            dataIndex: 'eventName',
            key: 'eventName',
            render: text => <span className="font-semibold text-gray-700">{text}</span>
        },
        {
            title: 'Scope',
            dataIndex: 'scope',
            key: 'scope',
            filters: [
                { text: 'Clinical', value: 'clinical' },
                { text: 'Drug', value: 'drug' },
                { text: 'Unknown', value: 'Unknown' },
            ],
            onFilter: (value, record) => (record.scope || 'Unknown') === value,
            render: val => (val ? val.charAt(0).toUpperCase() + val.slice(1) : <span className="text-gray-400">Unknown</span>),
        },
        {
            title: 'Region / Country',
            dataIndex: 'region',
            key: 'region',
            render: val => val || <span className="text-gray-400">Unknown</span>,
        },
        {
            title: 'Total Delegates',
            dataIndex: 'totalDelegates',
            key: 'totalDelegates',
            sorter: (a, b) => (a.totalDelegates || 0) - (b.totalDelegates || 0),
        },
        {
            title: 'Total Sponsors',
            dataIndex: 'totalSponsors',
            key: 'totalSponsors',
            sorter: (a, b) => (a.totalSponsors || 0) - (b.totalSponsors || 0),
        },
        {
            title: 'Total Attendees',
            dataIndex: 'totalAttendees',
            key: 'totalAttendees',
            sorter: (a, b) => a.totalAttendees - b.totalAttendees
        },
        {
            title: 'Total Meetings',
            dataIndex: 'totalMeetings',
            key: 'totalMeetings',
            sorter: (a, b) => a.totalMeetings - b.totalMeetings
        },
        {
            title: 'Pending Meetings',
            dataIndex: 'pendingMeetings',
            key: 'pendingMeetings',
            sorter: (a, b) => (a.pendingMeetings || 0) - (b.pendingMeetings || 0),
        },
        {
            title: 'Acceptance Rate',
            dataIndex: 'acceptanceRate',
            key: 'acceptanceRate',
            render: val => {
                let color = val >= 50 ? 'green' : (val >= 20 ? 'orange' : 'red');
                return <Tag color={color}>{val}%</Tag>;
            },
            sorter: (a, b) => a.acceptanceRate - b.acceptanceRate
        },
        {
            title: 'Rejection Rate',
            dataIndex: 'rejectionRate',
            key: 'rejectionRate',
            render: val => `${val}%`
        },
        {
            title: 'Total Show-ups (All)',
            dataIndex: 'totalShowUps',
            key: 'totalShowUps',
            sorter: (a, b) => (a.totalShowUps || 0) - (b.totalShowUps || 0),
        },
        {
            title: 'Delegate Show-ups (All Days)',
            dataIndex: 'delegateShowUps',
            key: 'delegateShowUps',
            sorter: (a, b) => (a.delegateShowUps || 0) - (b.delegateShowUps || 0),
        },
        {
            title: 'Delegate Day 1 Attendance',
            dataIndex: 'delegateDay1Attendance',
            key: 'delegateDay1Attendance',
        },
        {
            title: 'Delegate Day 2 Attendance',
            dataIndex: 'delegateDay2Attendance',
            key: 'delegateDay2Attendance',
        },
    ];

    return (
        <div className="animate-fade-in">
            <div className="mb-8 bg-white p-6 rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex-1">
                        <span className="block mb-2 font-semibold text-gray-700">Select Multiple Events to Compare:</span>
                        <Select
                            mode="multiple"
                            allowClear
                            style={{ width: '100%', maxWidth: '800px' }}
                            placeholder="Please select events"
                            onChange={(vals) => setSelectedEvents(vals)}
                            loading={isLoadingEvents}
                            showSearch
                            size="large"
                            className="w-full"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {events.map((e) => (
                                <Option key={e._id} value={e._id}>{e.name}</Option>
                            ))}
                        </Select>
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        disabled={!tableData || tableData.length === 0}
                        onClick={handleExport}
                        className="bg-primary hover:bg-yellow-600 border-none shadow-md hover:shadow-lg transition-all"
                    >
                        Export Comparison Data
                    </Button>
                </div>
            </div>

            {tableData.length > 0 && regionPieData.length > 0 && (
                <div className="mb-8 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="font-semibold mb-4 text-gray-700">Events by Region / Location</h3>
                    <div style={{ width: "100%", height: 300 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={regionPieData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    label
                                >
                                    {regionPieData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${entry.name}-${index}`}
                                            fill={pieColors[index % pieColors.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {isLoadingComparison ? (
                <div className="flex justify-center p-20"><Spin size="large" /></div>
            ) : selectedEvents.length > 0 ? (
                tableData.length > 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden transition-all duration-300">
                        <Table
                            dataSource={tableData}
                            columns={columns}
                            rowKey="eventId"
                            pagination={false}
                            rowClassName="hover:bg-gray-50/50 transition-colors"
                        />
                    </div>
                ) : (
                    <div className="text-center text-gray-400 mt-12 bg-white p-12 rounded-2xl border border-gray-100 shadow-sm">
                        <p className="text-lg">No comparison data available for the selected events.</p>
                    </div>
                )
            ) : (
                <div className="text-center text-gray-400 mt-20 flex flex-col items-center">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="mb-4 text-gray-300">
                        <path d="M19 15H15V19H19V15ZM21 3H3C1.9 3 1 3.9 1 5V19C1 20.1 1.9 21 3 21H21C22.1 21 23 20.1 23 19V5C23 3.9 22.1 3 21 3ZM21 19H3V5H21V19ZM13 15H9V19H13V15ZM19 9H15V13H19V9ZM13 9H9V13H13V9ZM7 9H3V13H7V9ZM7 15H3V19H7V15Z" fill="currentColor" />
                    </svg>
                    <p className="text-lg">Select two or more events to see a side-by-side comparison.</p>
                </div>
            )}
        </div>
    );
};

export default EventComparison;
